import { ReactElement } from 'react';

import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import GratiIconSwitch from './GratiIconSwitch';

export interface GratiPublishSwitchProps {
  isPublished: boolean;
  /** Turning the switch on. */
  onPublish: () => void;
  /** Turning the switch off. The caller decides whether to confirm first (see GratiSection). */
  onUnpublish: () => void;
  disabled?: boolean;
  /** id of the text saying why it's disabled, read as the switch's description. */
  describedBy?: string;
}

const GratiPublishSwitchRoot = styled('label', {
  name: 'GratiPublishSwitch',
  slot: 'root',
  overridesResolver: (_props, styles) => styles.root,
})(() => ({}));

/**
 * The admin's publish control, which always says the current state in words: "Draft" with the
 * eye-off thumb, "Published" with the eye. The word is the state and the switch is the action, so
 * neither can be read without the other. Its accessible name stays "Published" in both states, so
 * a screen reader hears "Published, off" rather than a name that changes with the state.
 */
export default function GratiPublishSwitch({ isPublished, onPublish, onUnpublish, disabled, describedBy }: GratiPublishSwitchProps): ReactElement {
  return (
    <GratiPublishSwitchRoot>
      <Typography variant="labelLarge" component="span" aria-hidden>
        {isPublished ? 'Published' : 'Draft'}
      </Typography>
      <GratiIconSwitch
        variant="visibility"
        icon={<VisibilityOffIcon />}
        checkedIcon={<VisibilityIcon />}
        checked={isPublished}
        disabled={disabled}
        onChange={(_, checked) => (checked ? onPublish() : onUnpublish())}
        slotProps={{ input: { 'aria-label': 'Published', 'aria-describedby': describedBy } }}
      />
    </GratiPublishSwitchRoot>
  );
}
